// Configuração de Prazos - Bloco 9
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, Save, Clock, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';

const API_URL = window.location.origin;

const TIPOS_OBRIGATORIOS = ['APA', 'Trabalho', 'Prontidão', 'Avaliação Trimestral'];
const TIPOS_OPCIONAIS = ['Recuperação Trimestral', 'Recuperação Paralela', 'Recuperação Final', 'Média Final'];

export function ConfiguracaoPrazos({ year, trimestre }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [year, trimestre]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/configuracao-prazos?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pendencias-academicas/configuracao-prazos?year=${year}&trimestre=${encodeURIComponent(trimestre)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config.prazos)
        }
      );
      
      if (res.ok) {
        toast.success('Configuração salva com sucesso!');
      } else {
        toast.error('Erro ao salvar configuração');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const updatePrazo = (tipo, field, value) => {
    setConfig(prev => ({
      ...prev,
      prazos: {
        ...prev.prazos,
        [tipo]: {
          ...prev.prazos[tipo],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6" data-testid="configuracao-prazos">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl text-white">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Configuração de Prazos</h2>
            <p className="text-slate-500 text-sm">{trimestre} - {year}</p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-purple-600 to-purple-500"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salvar Configurações
        </Button>
      </div>

      {/* Tipos Obrigatórios */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Avaliações Obrigatórias
          </CardTitle>
          <CardDescription>
            Estes tipos de avaliação são esperados para todos os alunos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {TIPOS_OBRIGATORIOS.map((tipo) => {
              const prazo = config.prazos?.[tipo] || { prazo_dias: 30, margem_tolerancia: 7, is_obrigatorio: true };
              
              return (
                <div key={tipo} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-red-100 text-red-700">{tipo}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={prazo.is_obrigatorio}
                        onCheckedChange={(checked) => updatePrazo(tipo, 'is_obrigatorio', checked)}
                      />
                      <Label className="text-sm text-slate-600">Obrigatório</Label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Prazo (dias)</Label>
                      <Input
                        type="number"
                        value={prazo.prazo_dias}
                        onChange={(e) => updatePrazo(tipo, 'prazo_dias', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Tolerância (dias)</Label>
                      <Input
                        type="number"
                        value={prazo.margem_tolerancia}
                        onChange={(e) => updatePrazo(tipo, 'margem_tolerancia', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tipos Opcionais */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            Avaliações Condicionais
          </CardTitle>
          <CardDescription>
            Estes tipos dependem da situação individual do aluno
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {TIPOS_OPCIONAIS.map((tipo) => {
              const prazo = config.prazos?.[tipo] || { prazo_dias: 45, margem_tolerancia: 14, is_obrigatorio: false };
              
              return (
                <div key={tipo} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">{tipo}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={prazo.is_obrigatorio}
                        onCheckedChange={(checked) => updatePrazo(tipo, 'is_obrigatorio', checked)}
                      />
                      <Label className="text-sm text-slate-600">Obrigatório</Label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Prazo (dias)</Label>
                      <Input
                        type="number"
                        value={prazo.prazo_dias}
                        onChange={(e) => updatePrazo(tipo, 'prazo_dias', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Tolerância (dias)</Label>
                      <Input
                        type="number"
                        value={prazo.margem_tolerancia}
                        onChange={(e) => updatePrazo(tipo, 'margem_tolerancia', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="text-sm text-green-700">
              As configurações de prazo afetam o cálculo de status (atrasado/crítico) nas pendências.
              A margem de tolerância é adicionada ao prazo antes de considerar como atraso.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
